import { Checkbox, FormControlLabel } from '@mui/material';
import theme from '~/theme.js';
import InputNumber from 'Components/Forms/InputNumber';
import Icon from 'Components/Icon';
import Table from 'Components/SimpleTable';
import React from 'react';
import icons from 'utils/ui/icons';
import { renderOkStatus } from '../renderUtils';
import { compactInputSx } from '../reportStyles';
import { CURRENT_KINDS, displayRatio, formatRealtimeValue, formatVariation, getMeasurementFromReadingKindName, getMeasurementRatio, getRangeNotes, getRealtimeValue, getVariationNote, getVariationValue, isFilled, isVariationWithinLimit, liveValue } from '../utils';

const renderIncompleteStatus = () => <strong style={{ color: theme.palette.text.disabled }}>INCOMPLETE</strong>;
const renderNotApplicableStatus = () => <strong style={{ color: theme.palette.pins.ignore }}>N/A</strong>;

// Beyond ±100% the exact figure is noise — a tiny system value makes the ratio absurd.
const formatCappedVariation = value => {
    if (value > 100) {
        return '>100%';
    }
    if (value < -100) {
        return '<-100%';
    }
    return formatVariation(value);
};

// A declared absence travels as a null under its key — a fact of the reading, not a hole in it.
const NotApplicable = ({ checked, disabled, onChange }) => (
    <FormControlLabel
        control={(
            <Checkbox
                checked={checked}
                checkedIcon={<Icon icon={icons.checkSquare} size="sm"/>}
                disabled={disabled}
                icon={<Icon icon={icons.square} size="sm"/>}
                onChange={onChange}
                sx={{ padding: '0 4px' }}
            />
        )}
        label="N/A"
        sx={{ margin: 0, '& .MuiFormControlLabel-label': { fontSize: '0.65rem' } }}
    />
);

const NumberBox = ({ disabled, onChange, value }) => (
    <div style={{ maxWidth: 120 }}>
        <InputNumber disabled={disabled} input={{ value, onChange }} label="" margin="dense" meta={{}} sx={compactInputSx}/>
    </div>
);

const MeterBox = ({ disabled, notApplicable, onChange, onNotApplicableChange, value }) => (
    <div style={{ alignItems: 'center', display: 'flex', gap: 4 }}>
        <NumberBox disabled={disabled || notApplicable} onChange={onChange} value={notApplicable ? '' : value}/>
        <NotApplicable checked={notApplicable} disabled={disabled} onChange={onNotApplicableChange}/>
    </div>
);

const displayValue = value => (value === null ? 'N/A' : value);

const auxiliaryStatus = (notApplicable, hasValue, notes) => {
    if (notApplicable) {
        return renderNotApplicableStatus();
    }
    return hasValue ? renderOkStatus(notes.length === 0) : renderIncompleteStatus();
};

// A row the meter cannot answer is not complete — but a clamp outside the CT is NOT OK all the same.
const primaryStatus = (systemNotApplicable, filled, ok, rangeNotes) => {
    if (systemNotApplicable) {
        return rangeNotes ? renderOkStatus(false) : renderIncompleteStatus();
    }
    return filled ? renderOkStatus(ok) : renderIncompleteStatus();
};

// One table, one reading: a row per profile kind — primary rows compare system against
// technician, auxiliary rows carry the single meter-face value.
const CombinedTable = props => {
    const { auxiliary, device, loading, manualValues, measurements, onlineKinds, otherMeasurements, primary } = props;
    const { readings, readingsById, realtime, storedMeterValues, systemValues, technicianValues, unitSymbols, validated } = props;
    const { onManualChange, onManualNotApplicableChange, onSystemChange, onSystemNotApplicableChange, onTechnicianChange } = props;

    const columns = [
        { label: 'Measurement', dataKey: 'measurement', width: '15%' },
        { label: 'Status', dataKey: 'status', width: '10%' },
        { label: 'Ratio (Unit)', dataKey: 'ratio', width: '7%' },
        { label: 'System', dataKey: 'system', width: '15%' },
        { label: 'Technician', dataKey: 'technician', width: '15%' },
        { label: 'Notes', dataKey: 'notes', width: '38%' }
    ];

    // Two verdicts on one row: the measurements agree, and the clamp reads within the CT rating.
    const primaryRow = kind => {
        const systemValue = systemValues[kind];
        const technicianValue = technicianValues[kind];
        const systemNotApplicable = systemValue === null;
        const variationValue = getVariationValue(systemValue, technicianValue);
        const variationNote = getVariationNote(variationValue);
        const rangeNotes = isFilled(technicianValue) ? getRangeNotes(parseFloat(technicianValue), kind, device.extra.ti_current) : '';
        const filled = isFilled(systemValue) && isFilled(technicianValue);
        const status = primaryStatus(systemNotApplicable, filled, isVariationWithinLimit(variationValue) && !rangeNotes, rangeNotes);
        const notes = [systemNotApplicable && 'No system value', variationNote && `${variationNote} (${formatCappedVariation(variationValue)})`, rangeNotes].filter(Boolean).join('; ');

        let systemCell = displayValue(systemValue);
        if (!validated && !onlineKinds.includes(kind)) {
            systemCell = (
                <MeterBox disabled={loading} notApplicable={systemNotApplicable} onChange={onSystemChange(kind)} onNotApplicableChange={onSystemNotApplicableChange(kind)} value={systemValue}/>
            );
        }

        let technicianCell = technicianValue;
        if (!validated) {
            technicianCell = <NumberBox disabled={loading} onChange={onTechnicianChange(kind)} value={technicianValue}/>;
        }

        const ratio = displayRatio(getMeasurementFromReadingKindName(kind, measurements, readings), readingsById, unitSymbols);

        return { id: kind, measurement: CURRENT_KINDS.includes(kind) ? `${kind} (TI ${device.extra.ti_current}A)` : kind, status, system: systemCell, ratio, technician: technicianCell, notes };
    };

    const auxiliaryRow = kind => {
        const measurement = getMeasurementFromReadingKindName(kind, measurements, readings);
        let value = manualValues[kind];
        let isManual = true;

        if (validated) {
            value = storedMeterValues[kind];
            isManual = false;
        } else {
            const match = liveValue(realtime, measurement);
            if (match) {
                value = formatRealtimeValue(match.value, getMeasurementRatio(measurement, readingsById));
                isManual = false;
            }
        }

        const notApplicable = value === null;
        const hasValue = isFilled(value);
        const notes = hasValue && !notApplicable ? getRangeNotes(parseFloat(value), kind, device.extra.ti_current) : '';
        const status = auxiliaryStatus(notApplicable, hasValue, notes);

        let systemCell = displayValue(value);
        if (isManual) {
            systemCell = (
                <MeterBox disabled={loading} notApplicable={notApplicable} onChange={onManualChange(kind)} onNotApplicableChange={onManualNotApplicableChange(kind)} value={value}/>
            );
        }

        return { id: kind, measurement: kind, status, system: systemCell, ratio: displayRatio(measurement, readingsById, unitSymbols), technician: '', notes };
    };

    // Context rows, no pen: bystander measurements show their live value and nothing else.
    const otherRow = measurement => ({
        id: measurement.id,
        measurement: readingsById[measurement.reading_id]?.reading_kind_name,
        status: '',
        system: getRealtimeValue(realtime, measurement, readingsById),
        ratio: displayRatio(measurement, readingsById, unitSymbols),
        technician: '',
        notes: ''
    });

    const rows = [...primary.map(primaryRow), ...auxiliary.map(auxiliaryRow), ...(validated ? [] : otherMeasurements.map(otherRow))];

    return (
        <Table columns={columns} headerHeight={28} loading={loading} rowHeight={28} rows={rows} rowsPerPage={rows.length}/>
    );
};

export default CombinedTable;
